/**
 * UPN Override utilities for impersonation
 * Allows setting a UPN in localStorage to override the current user's UPN
 */

const UPN_OVERRIDE_KEY = 'upn';

/**
 * Get UPN override from localStorage
 * @returns The UPN override string, or undefined if not set
 */
export function getUpnOverride(): string | undefined {
    try {
        const overrideUpn = localStorage.getItem(UPN_OVERRIDE_KEY);
        return overrideUpn || undefined;
    } catch (error) {
        console.error('Error reading UPN override from localStorage:', error);
        return undefined;
    }
}

/**
 * Set UPN override in localStorage
 * @param upn The UPN to set as override, or undefined/null to clear
 */
export function setUpnOverride(upn: string | undefined | null): void {
    try {
        if (upn) {
            localStorage.setItem(UPN_OVERRIDE_KEY, upn);
        } else {
            localStorage.removeItem(UPN_OVERRIDE_KEY);
        }
    } catch (error) {
        console.error('Error setting UPN override in localStorage:', error);
    }
}

/**
 * Clear UPN override from localStorage
 */
export function clearUpnOverride(): void {
    setUpnOverride(undefined);
}

/**
 * Try to get UPN override with domain suffix handling
 * Handles different UPN formats:
 * - If includes '@', use as-is
 * - If starts with 'S', append '@students.duvalschools.org'
 * - If starts with 'P-', append '@public.duvalschools.org'
 * - Otherwise, warn and return as-is
 * 
 * @returns The formatted UPN override, or undefined if not set
 */
export function tryGetUpnOverride(): string | undefined {
    const overrideUpn = getUpnOverride();

    if (!overrideUpn) {
        return undefined;
    }

    console.warn('UPN override active:', overrideUpn);

    // Handle different UPN formats
    if (overrideUpn.includes('@')) {
        return overrideUpn;
    } else if (overrideUpn[0] === 'S') {
        return overrideUpn + '@students.duvalschools.org';
    } else if (overrideUpn[0] === 'P' && overrideUpn[1] === '-') {
        return overrideUpn + '@public.duvalschools.org';
    } else {
        console.warn('UPN does not include @, maybe malformed:', overrideUpn);
        return overrideUpn;
    }
}

/**
 * Check if UPN override is currently active
 * @returns true if UPN override is set, false otherwise
 */
export function hasUpnOverride(): boolean {
    return getUpnOverride() !== undefined;
}

